import { useContext, useEffect } from "react";

import { useNavigate, useParams } from "react-router-dom";
import { UserContext } from "@context/user";

// Keeps the URL's :brandId honest against the known brand list — bounces
// back to brand 0's dashboard if it doesn't match anything (bad/stale link,
// hand-edited URL, ...), and otherwise keeps the topbar switcher in sync
// with whichever valid brand the URL points at.
//
// It also locks the workspace: a brand that hasn't chosen how it pays publishers
// yet is sent to its setup gate from every page. Only the gate itself passes
// `allowPendingSetup` — anything else would loop.
//
// `requiresDebitNotes` is for the pages the nav hides from a self-payout brand:
// hiding the link is not enough, a hand-typed URL has to bounce too.
// `requiresSelfPayout` is its mirror — the disbursements queue only belongs to a
// brand that pays its publishers itself.
const useBrandRouteGuard = ({ allowPendingSetup = false, requiresDebitNotes = false, requiresSelfPayout = false } = {}) => {
    const { brandId } = useParams();
    const navigate = useNavigate();
    const { brands, brandsLoaded, activeBrandId, setActiveBrandId } = useContext(UserContext);
    const brand = brands.find((item) => item.id === brandId);
    const isValid = Boolean(brand);
    const setupPending = Boolean(brand?.setupPending);

    // Whether this brand may actually be on this page. The redirects below only run
    // in an effect, so a page that fetches on mount would still fire its request for
    // a brand it is about to be bounced away from — switching brands rewrites the
    // :brandId in place, which re-runs the data hook rather than unmounting the page.
    // Pages gate their fetch on this instead.
    const allowed = brandsLoaded
        && isValid
        && (allowPendingSetup || !setupPending)
        && (!requiresDebitNotes || Boolean(brand.hasDebitNotes))
        && (!requiresSelfPayout || !brand.hasDebitNotes);

    useEffect(() => {
        if (!brands.length) return;
        if (!isValid) {
            navigate(`/brand/${brands[0].id}/dashboard`, { replace: true });
            return;
        }
        if (brandId !== activeBrandId) setActiveBrandId(brandId);
        if (setupPending && !allowPendingSetup) {
            navigate(`/brand/${brandId}/setup`, { replace: true });
            return;
        }
        if (requiresDebitNotes && !brand.hasDebitNotes) {
            navigate(`/brand/${brandId}/dashboard`, { replace: true });
            return;
        }
        if (requiresSelfPayout && brand.hasDebitNotes) {
            navigate(`/brand/${brandId}/dashboard`, { replace: true });
        }
    }, [brands, isValid, brandId, setupPending]); // eslint-disable-line react-hooks/exhaustive-deps

    return { brandId, brand, isValid, setupPending, allowed };
};

export default useBrandRouteGuard;
